import type { ReactElement } from "react"
import { ColumnGrid } from "@/components/website-builder/ColumnGrid"
import { getSectionVariant } from "./variants"
import { TestimonialsSpotlight, TestimonialsWall } from "./testimonials_variants"
import { wrapWithBackgroundIfNeeded, type BlockComponentProps, HEADING_COLOR } from "./shared"

/**
 * `grid` (default / unknown variant) is the original layout, unchanged for all
 * existing sections. All layouts keep the shared background-image wrapper so
 * `content.background_image` behaves the same regardless of variant.
 */
export function TestimonialsBlock(props: BlockComponentProps) {
  switch (getSectionVariant(props.content)) {
    case "spotlight":
      return wrapWithBackgroundIfNeeded(props.content, <TestimonialsSpotlight {...props} />) as ReactElement
    case "wall":
      return wrapWithBackgroundIfNeeded(props.content, <TestimonialsWall {...props} />) as ReactElement
    default:
      return <TestimonialsGridDefault {...props} />
  }
}

function TestimonialsGridDefault({ content, resolved }: BlockComponentProps) {
  const th = (content?.heading as string) ?? "What people say"
  const items = (content?.testimonials as Array<{ quote?: string; author?: string; role?: string }>) ?? []
  const thColumns = Math.min(2, Math.max(1, (content?.columns as number) ?? 2))
  const testSectionClass = resolved.hasOverrides
    ? `px-4 @sm:px-6 @md:px-8 ${resolved.paddingClass || "py-8"} ${resolved.cornerClass} ${resolved.alignClass} bg-muted/30`.trim()
    : "px-4 py-8 @sm:px-6 @md:px-8 bg-muted/30"
  return wrapWithBackgroundIfNeeded(
    content,
    <section className={testSectionClass} style={resolved.sectionStyle}>
      <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: HEADING_COLOR }}>
        {th}
      </h2>
      <div className="max-w-4xl mx-auto">
        <ColumnGrid columns={thColumns} itemCount={items.length}>
          {items.map((item, i) => (
            <blockquote key={i} className="p-4 rounded-lg bg-background border">
              {item.quote && <p className="italic mb-2">&quot;{item.quote}&quot;</p>}
              {item.author && <footer className="text-sm font-medium">{item.author}</footer>}
              {item.role && <cite className="text-muted-foreground text-xs block">{item.role}</cite>}
            </blockquote>
          ))}
        </ColumnGrid>
      </div>
    </section>
  ) as ReactElement
}
